import { InfoIcon, PlusIcon, Trash2Icon } from "lucide-react"
import { type ReactNode, useState } from "react"
import { toast } from "sonner"
import { CopyableField } from "~/components/CopyableField"
import { SettingRow } from "~/components/settings/SettingRow"
import { Button, TooltipButton } from "~/components/ui/button"
import { Input } from "~/components/ui/input"
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from "~/components/ui/dialog"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from "~/components/ui/select"
import { type FormShareAccess, shareDurationValues } from "~/lib/enums"
import { confirmAction } from "~/lib/native-dialog"
import { api } from "~/trpc/react"

const durationLabels: Record<(typeof shareDurationValues)[number], string> = {
	"24h": "24 hours",
	"72h": "72 hours",
	"1w": "1 week",
	"1mo": "1 month",
	forever: "Forever"
}

export function FormShareDialog({
	teamId,
	formId,
	children
}: {
	teamId: string
	formId: string
	children: ReactNode
}) {
	const [open, setOpen] = useState(false)
	const [adding, setAdding] = useState(false)
	const [access, setAccess] = useState<FormShareAccess>("org")
	const [duration, setDuration] =
		useState<(typeof shareDurationValues)[number]>("1w")
	const [selectedHostname, setHostname] = useState<string | null>()
	const [path, setPath] = useState("")
	const utils = api.useUtils()
	const shareDomains = api.forms.submitDomains.useQuery(
		{ teamId },
		{ enabled: open }
	)
	const accessLabels = {
		anyone: "Anyone",
		team: shareDomains.data?.teamName
			? `${shareDomains.data.teamName} members only`
			: "Members only",
		org: shareDomains.data?.organizationName
			? `${shareDomains.data.organizationName} members only`
			: "Members only"
	}
	const baseUrl = shareDomains.data?.baseUrl
	const domains = shareDomains.data?.domains ?? []
	const hostname =
		domains.length > 1 && selectedHostname === null
			? null
			: (domains.find((domain) => domain.hostname === selectedHostname)
					?.hostname ??
				domains[0]?.hostname ??
				null)
	const shares = api.forms.shares.useQuery(
		{ teamId, formId },
		{ enabled: open }
	)
	const hasShares = Boolean(shares.data?.length)
	const createShare = api.forms.createShare.useMutation({
		onSuccess: async () => {
			await utils.forms.shares.invalidate({ teamId, formId })
			setAdding(false)
			setPath("")
			toast("Link created")
		},
		onError: (error) => toast.error(error.message)
	})
	const deleteShare = api.forms.deleteShare.useMutation({
		onSuccess: async () => {
			await utils.forms.shares.invalidate({ teamId, formId })
			toast("Link removed")
		},
		onError: (error) => toast.error(error.message)
	})
	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				setOpen(next)
				if (!next) setAdding(false)
			}}
		>
			<DialogTrigger asChild>{children}</DialogTrigger>
			<DialogContent size="md" aria-describedby={undefined}>
				<DialogHeader>
					<DialogTitle>Share form</DialogTitle>
				</DialogHeader>
				<div className="flex flex-col gap-4">
					{(shareDomains.error || shares.error) && (
						<p role="alert" className="text-sm text-destructive">
							{shareDomains.error?.message ?? shares.error?.message}
						</p>
					)}
					{baseUrl &&
						(shares.data ?? []).map((share) => (
							<FormShareLinkRow
								key={share.id}
								url={share.url}
								accessLabel={accessLabels[share.access]}
								expiresAt={share.expiresAt}
								pending={deleteShare.isPending}
								onRemove={async () => {
									if (
										await confirmAction({
											title: "Remove link",
											message: "Remove this fill-out link?"
										})
									) {
										deleteShare.mutate({ teamId, formId, shareId: share.id })
									}
								}}
							/>
						))}
					{shares.isSuccess && baseUrl && (!hasShares || adding) && (
						<div className="flex flex-col gap-3">
							{domains.length > 1 && (
								<SettingRow label="Domain">
									<Select
										value={hostname ?? "__base__"}
										onValueChange={(value) =>
											setHostname(value === "__base__" ? null : value)
										}
									>
										<SelectTrigger aria-label="Domain">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="__base__">{baseUrl}</SelectItem>
											{domains.map((domain) => (
												<SelectItem key={domain.id} value={domain.hostname}>
													{domain.hostname}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</SettingRow>
							)}
							{hostname && (
								<SettingRow label="Link path">
									<div className="flex min-w-0 items-center gap-2">
										<span className="truncate text-xs text-text-sub">
											{hostname}/
										</span>
										<Input
											value={path}
											onChange={(event) =>
												setPath(
													event.target.value
														.toLowerCase()
														.replace(/[^a-z0-9-]/g, "")
												)
											}
											aria-label="Link path"
											placeholder="feedback"
											className="min-w-0 flex-1"
										/>
									</div>
								</SettingRow>
							)}
							<SettingRow label="Who can fill">
								<Select
									value={access}
									onValueChange={(next) => setAccess(next as FormShareAccess)}
								>
									<SelectTrigger aria-label="Who can fill">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{(["anyone", "team", "org"] as const).map((value) => (
											<SelectItem key={value} value={value}>
												{accessLabels[value]}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</SettingRow>
							<SettingRow label="Expires">
								<Select
									value={duration}
									onValueChange={(next) =>
										setDuration(next as (typeof shareDurationValues)[number])
									}
								>
									<SelectTrigger aria-label="Link expiry">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{shareDurationValues.map((value) => (
											<SelectItem key={value} value={value}>
												{durationLabels[value]}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</SettingRow>
							<div className="flex items-center gap-2">
								<Button
									pending={createShare.isPending}
									disabled={!shareDomains.isSuccess || shareDomains.isFetching}
									icon={<PlusIcon />}
									onClick={() =>
										createShare.mutate({
											teamId,
											formId,
											access,
											duration,
											hostname,
											...(hostname && path ? { path } : {})
										})
									}
								>
									Create link
								</Button>
								{hasShares && (
									<Button
										variant="ghost"
										disabled={createShare.isPending}
										onClick={() => setAdding(false)}
									>
										Cancel
									</Button>
								)}
							</div>
						</div>
					)}
					{hasShares && !adding && (
						<Button
							variant="outline"
							className="self-start"
							icon={<PlusIcon />}
							onClick={() => setAdding(true)}
						>
							Add link
						</Button>
					)}
				</div>
			</DialogContent>
		</Dialog>
	)
}

export function FormShareLinkRow({
	url,
	accessLabel,
	expiresAt,
	pending,
	onRemove
}: {
	url: string
	accessLabel: string
	expiresAt: Date | null
	pending?: boolean
	onRemove: () => void
}) {
	return (
		<div className="flex min-w-0 items-center gap-2">
			<div className="min-w-0 flex-1">
				<CopyableField value={url} label="Form link" />
			</div>
			<TooltipButton
				tooltip={`${accessLabel} · ${expiresAt ? `Expires ${expiresAt.toLocaleString()}` : "Never expires"}`}
				aria-label="Link details"
				variant="ghost"
				icon={<InfoIcon />}
			/>
			<TooltipButton
				tooltip="Remove link"
				variant="destructive-ghost"
				pending={pending}
				icon={<Trash2Icon />}
				onClick={onRemove}
			/>
		</div>
	)
}
